import React, { useState, useEffect, Suspense } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePerm } from '../hooks/usePerm';
import { usePlan } from '../hooks/usePlan';
import { useTheme } from '../hooks/useTheme';
import { api } from '../api/client';
import { useNotifications } from '../hooks/useNotifications';
import NotificationCenter, { NotificationBell } from './NotificationCenter';
import { ChannelProvider } from './channel/ChannelProvider';
import ChannelHost from './channel/ChannelHost';
import { Analytics } from '../analytics';
import { useTranslation } from 'react-i18next';
import DemoBanner from './DemoBanner';
import DemoTour from './demo/DemoTour';
import SubscriptionBanner from './SubscriptionBanner';
import CelebrationModal from './CelebrationModal';
import { useMilestone } from '../hooks/useMilestone';
import { BRAND_NAME, SUBSCRIPTION_TAB_ENABLED } from '../branding';

export default function Layout() {
  const { user, logout } = useAuth();
  const { can, canAny, isOwnScope } = usePerm();
  const { hasFeature } = usePlan();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { t, i18n } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [overdueCount, setOverdueCount] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const [smallLogo, setSmallLogo] = useState(null);
  const [demoExitModal, setDemoExitModal] = useState(false);
  const [demoPassword, setDemoPassword] = useState('');
  const [demoPasswordConfirm, setDemoPasswordConfirm] = useState('');
  const [demoPasswordSaving, setDemoPasswordSaving] = useState(false);
  const [demoPasswordError, setDemoPasswordError] = useState('');
  const [darkLogo, setDarkLogo] = useState(null);
  const [orgName, setOrgName] = useState(BRAND_NAME);
  const [logoVersion, setLogoVersion] = useState(() => Date.now());

  const { notifications, unread, loading, markAllRead, markOneRead, refresh: refreshNotifications } = useNotifications();

  // Мини-празднования: рубежи орги (100 Работ и далее). Живёт в Layout, а не на
  // странице Работ, — рубеж может взять коллега, и splash должен догнать нас
  // на любом экране.
  const { milestone, dismiss: dismissMilestone } = useMilestone(user);

  // Сигнал для демо-тура: открыт/закрыт Центр уведомлений (плашка-подсказка
  // переезжает вниз, чтобы не перекрывать само уведомление).
  useEffect(() => {
    window.dispatchEvent(new CustomEvent(notifOpen ? 'notif-center-open' : 'notif-center-close'));
  }, [notifOpen]);

  // Демо-тур может попросить закрыть Центр уведомлений (напр. «Ещё осмотрюсь»).
  useEffect(() => {
    const onClose = () => setNotifOpen(false);
    window.addEventListener('demo-close-notif-center', onClose);
    return () => window.removeEventListener('demo-close-notif-center', onClose);
  }, []);

  const loadLogos = () => {
    api.getOrganization().then(org => {
      setSmallLogo(org?.logo_small_path || null);
      setDarkLogo(org?.logo_dark_path || null);
      setOrgName(org?.name || BRAND_NAME);
      setLogoVersion(Date.now());
      // Сразу прописываем favicon.ico тенанта
      if (org?.favicon_path) {
        const v = Date.now();
        document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']").forEach(el => el.remove());
        const ico = document.createElement('link');
        ico.rel = 'icon'; ico.type = 'image/x-icon'; ico.href = `${org.favicon_path}?v=${v}`;
        document.head.appendChild(ico);
      }
      // ⚠️ apple-touch-icon — только из apple_icon_path (квадрат 512, белый
      // непрозрачный фон). Малый логотип сюда ставить нельзя: iOS кладёт
      // прозрачность на ЧЁРНЫЙ и растягивает не-квадрат до квадрата.
      // Нет файла у тенанта — тег не трогаем вовсе: дефолт инстанса лучше
      // битой иконки. См. backend/services/brandIcons.js.
      if (org?.apple_icon_path) {
        const v = Date.now();
        document.querySelectorAll("link[rel='apple-touch-icon']").forEach(el => el.remove());
        const apple = document.createElement('link');
        apple.rel = 'apple-touch-icon';
        apple.href = `${org.apple_icon_path}?v=${v}`;
        document.head.appendChild(apple);
      }
    }).catch(() => {});
  };

  const loadBadges = () => {
    api.getDashboard().then(d => {
      setOverdueCount(d.overdue?.length || 0);
      setTodayCount(d.today?.length || 0);
    }).catch(() => {});
  };

  // Вычисляем итоговый бейдж
  const badgeCount = overdueCount > 0 ? overdueCount : todayCount;
  const badgeClass = overdueCount > 0 ? '' : 'orange';

  useEffect(() => {
    loadBadges();
    loadLogos();

    // Polling бейджа каждые 30 сек — синхронно с useNotifications
    const badgeTimer = setInterval(() => loadBadges(), 30_000);

    // Слушаем событие обновления логотипа из Settings
    window.addEventListener('logo-updated', loadLogos);
    // Слушаем событие обновления задач (после отметки выполненной)
    window.addEventListener('tasks-updated', loadBadges);

    return () => {
      clearInterval(badgeTimer);
      window.removeEventListener('logo-updated', loadLogos);
      window.removeEventListener('tasks-updated', loadBadges);
    };
  }, []);

  // iOS 26 Safari: плавающая панель создаёт зазор между app-layout и низом экрана.
  // Растягиваем app-layout до outerHeight и добавляем padding-bottom на main-content
  // чтобы под панелью всегда был фон страницы, а не тёмный контент (Liquid Glass).
  useEffect(() => {
    const applyHeight = () => {
      const layout = document.querySelector('.app-layout');
      const main = document.querySelector('.main-content');
      if (!layout) return;
      // Хак только для мобильного Safari/PWA: outerHeight > innerHeight из-за
      // плавающей панели iOS 26. На десктопе outerHeight всегда > innerHeight
      // (включает chrome браузера) — не применяем, чтобы не обрезать sidebar-user.
      const isMobile = window.innerWidth <= 768;
      if (isMobile && window.outerHeight && window.outerHeight > window.innerHeight) {
        const diff = window.outerHeight - window.innerHeight;
        layout.style.height = window.outerHeight + 'px';
        if (main) main.style.paddingBottom = diff + 'px';
      } else {
        layout.style.height = '';
        if (main) main.style.paddingBottom = '';
      }
    };
    applyHeight();
    window.addEventListener('resize', applyHeight);
    return () => window.removeEventListener('resize', applyHeight);
  }, []);

  // Обновляем title вкладки — показываем только имя тенанта
  useEffect(() => {
    document.title = orgName ? `${orgName} · ${BRAND_NAME}` : BRAND_NAME;
  }, [orgName]);

  // Обновляем favicon из лого организации.
  // ⚠️ apple-touch-icon здесь НЕ трогаем: он ставится в loadLogos из
  // apple_icon_path, малый логотип для iOS не годится (см. комментарий там).
  useEffect(() => {
    if (!smallLogo) return;
    // Cache-buster чтобы браузер подхватил новый логотип
    const url = `${smallLogo}?v=${Date.now()}`;
    // Удаляем существующие favicon-теги (apple-touch-icon не трогаем)
    document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']").forEach(el => el.remove());
    // favicon для браузера
    const favicon = document.createElement('link');
    favicon.rel = 'icon';
    favicon.type = 'image/png';
    favicon.href = url;
    document.head.appendChild(favicon);
  }, [smallLogo]);

  const handleLogout = async () => {
    if (user?.is_demo) {
      // Для демо показываем модал установки пароля
      setDemoPassword('');
      setDemoPasswordConfirm('');
      setDemoPasswordError('');
      setDemoExitModal(true);
      return;
    }
    logout();
    navigate('/login');
  };

  const handleDemoSetPassword = async () => {
    if (demoPassword.length < 6) {
      setDemoPasswordError(t('demo.exitModal.errorShort'));
      return;
    }
    if (demoPassword !== demoPasswordConfirm) {
      setDemoPasswordError(t('demo.exitModal.errorMismatch'));
      return;
    }
    setDemoPasswordSaving(true);
    try {
      await api.post('/demo/set-password', { password: demoPassword });
      await api.post('/demo/account-logout');
    } catch {}
    setDemoPasswordSaving(false);
    setDemoExitModal(false);
    logout();
    navigate('/login');
  };

  const handleDemoExitWithoutPassword = async () => {
    try { await api.post('/demo/account-logout'); } catch {}
    setDemoExitModal(false);
    logout();
    navigate('/login');
  };
  const closeSidebar = () => setSidebarOpen(false);

  // При открытом боковом меню на мобиле скрываем FAB-кнопки на картах
  // (напр. «Моё местоположение»), чтобы они не торчали над меню.
  useEffect(() => {
    document.body.classList.toggle('sidebar-open', sidebarOpen);
    return () => document.body.classList.remove('sidebar-open');
  }, [sidebarOpen]);

  // Флаг демо для компонентов вне дерева useAuth (напр. лайтбокс прячет FAB только в демо)
  useEffect(() => {
    document.body.classList.toggle('pm-demo', !!user?.is_demo);
  }, [user]);

  // Название роли: своё имя тенанта (двуязычное, /auth/me → role_name /
  // role_name_en) перебивает штатный перевод по ключу.
  const ownRoleName = String(i18n.language).startsWith('en')
    ? (user?.role_name_en || user?.role_name)
    : (user?.role_name || user?.role_name_en);
  const roleTitle = ownRoleName
    || (user?.role_key ? t(`perm.role.${user.role_key}`, user.role_key) : null)
    || t('roles.user');

  const isWorker = isOwnScope;

  const NavIcons = {
    dashboard: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
      </svg>
    ),
    clients: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    properties: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    tasks: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
      </svg>
    ),
    stats: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
      </svg>
    ),
    settings: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
    ),
    projects: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
        <line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/>
      </svg>
    ),
    help: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
    referral: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
      </svg>
    ),
    expenses: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
    // Сметы — планшет со строками: тот же образ, что у иконки ряда нумерации
    // «Смета» (📋) в Настройках, только в линейном стиле меню.
    quotes: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 2h6a1 1 0 0 1 1 1v2H8V3a1 1 0 0 1 1-1z"/>
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
        <line x1="8" y1="11" x2="16" y2="11"/><line x1="8" y1="15" x2="13" y2="15"/>
      </svg>
    ),
    // «Парк» — переносная техника (фаза 3 перемещения оборудования).
    fleet: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
        <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
      </svg>
    ),
    requests: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
        <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
      </svg>
    ),
  };

  // Меню строится по правам роли. Тумблеры модулей организации (clients_enabled
  // и т. п.) бэкенд уже учёл в снимке прав — здесь остаются только те, что не
  // привязаны к модулю реестра.
  const navItems = [
    // Дашборд (ИИ-советник + обзор) — первым пунктом и лендингом для всех ролей.
    // Узкой области отдаётся WorkerDashboard, остальным — DashboardInsights.
    { to: '/dashboard', label: t('nav.dashboard'), icon: NavIcons.dashboard },
    // Объекты выше Контактов: Объект — верхний уровень модели (Контакт теперь
    // сущность под Объектом), и меню должно повторять иерархию, а не историю.
    { to: '/properties', label: t('nav.properties'), icon: NavIcons.properties },
    ...(can('clients.view') ? [{ to: '/contacts', label: t('nav.clients'), icon: NavIcons.clients }] : []),
    ...(can('requests.view') ? [{ to: '/requests', label: t('nav.requests'), icon: NavIcons.requests }] : []),
    ...(can('projects.view') && hasFeature('projects') ? [{ to: '/projects', label: t('nav.projects'), icon: NavIcons.projects }] : []),
    // «Парк» — где сейчас вся переносная техника. Только у организаций с
    // включённым перемещением и у тех, кто может возить (equipment.move):
    // тумблер модуля не входит в снимок прав, поэтому проверяем его явно.
    ...(user?.equipment_mobility_enabled && can('equipment.move')
      ? [{ to: '/fleet', label: t('nav.fleet'), icon: NavIcons.fleet }] : []),
    { to: '/tasks', label: t('nav.tasks'), icon: NavIcons.tasks,
      badge: badgeCount > 0 ? badgeCount : null,
      badgeClass: badgeClass
    },
    // Сметы ниже Работ и Проектов: смета рождается внутри них, меню повторяет
    // иерархию. Дальше блоком идут деньги: Сметы → Расходы → Статистика.
    // Тумблер модуля организации бэкенд уже учёл в снимке прав — здесь только can().
    ...(can('quotes.view') ? [{ to: '/quotes', label: t('nav.quotes'), icon: NavIcons.quotes }] : []),
    ...(can('expenses.view') ? [{ to: '/expenses', label: t('nav.expenses'), icon: NavIcons.expenses }] : []),
    ...(can('dashboard.view') ? [{ to: '/stats', label: t('nav.stats'), icon: NavIcons.stats }] : []),
    // Настройки — это набор вкладок с разными гейтами, поэтому пункт меню
    // держится не на роли, а на наличии хотя бы одной доступной вкладки.
    // equipment.print_tags по дефолтам есть у Бригадира, а org_settings/team
    // у него нет: без этого условия право на печать наклеек было бы выдано,
    // но недостижимо — кнопка живёт внутри Настроек.
    // subscription.view — вкладка «Подписка» (если она есть на инстансе).
    ...(canAny('org_settings') || canAny('team') || can('equipment.print_tags')
        || (SUBSCRIPTION_TAB_ENABLED && can('subscription.view'))
      ? [{ to: '/settings', label: t('nav.settings'), icon: NavIcons.settings }] : []),
    // Биллинг тенантов переехал в Founder Dashboard (stat.fixty.io): кросс-
    // тенантный контроль внутри учётки одного тенанта был виден только из
    // Aimtex и путался с деньгами клиент → тенант (Zoho).
    { to: '/help', label: t('nav.help'), icon: NavIcons.help },
  ];

  // data-fab-ignore: шелл — это position:fixed во весь экран и он всегда в DOM.
  // Без метки автодетект оверлеев (hooks/useFabState) принял бы его за открытый
  // модал и погасил бы плавающую «+» навсегда. Метка действует ТОЛЬКО на сам
  // узел: модалы рендерятся внутри шелла, и наследуемое исключение отключило бы
  // детект во всём приложении.
  return (
    <ChannelProvider>
    <div className="app-layout" data-fab-ignore="">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }}
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <div className="logo-icon">
            {smallLogo || darkLogo ? (
              <img
                src={`${(theme === 'dark' ? (darkLogo || smallLogo) : smallLogo) || darkLogo}?v=${logoVersion}`}
                alt={t('common.logo')}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            )}
          </div>
          <div>
            <h1>{orgName}</h1>
            <span>{t('common.orgManagement')}</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-label">{t('nav.navigation')}</div>
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <span className="icon">{item.icon}</span>
                {item.label}
                {item.badge != null && (
                  <span className={`badge ${item.badgeClass || ''}`}>{item.badge}</span>
                )}
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="sidebar-user">
          <div
            className="user-info"
            onClick={() => { navigate('/profile'); closeSidebar(); }}
            title={t('common.myProfile')}
            style={{ cursor: 'pointer' }}
          >
            <div className="name">{user?.name}</div>
            <div className="role">{roleTitle}</div>
          </div>
          <button className="logout-btn" onClick={toggle} title={theme === 'dark' ? t('theme.light') : t('theme.dark')}>
            {theme === 'dark' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>
          <button className="logout-btn" onClick={handleLogout} title={t('common.logout')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        </div>
      </aside>

      {/* Desktop notification bell — fixed top-right above content */}
      <div className="desktop-notif-btn">
        <NotificationBell unread={unread} onClick={() => { Analytics.notifBellOpened({ unread_count: unread }); setNotifOpen(true); }} />
      </div>

      {/* Main */}
      <div className="main-content">
        {/* Mobile header */}
        <div className="mobile-header">
          <button className="hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{orgName}</span>
          <NotificationBell unread={unread} onClick={() => { Analytics.notifBellOpened({ unread_count: unread }); setNotifOpen(true); }} />
        </div>
        <DemoBanner />
        <SubscriptionBanner />
        {/* Suspense внутри Layout: при загрузке lazy-чанка страницы навигация
            остаётся на экране, спиннер только в области контента */}
        <Suspense fallback={
          <div className="loading-screen" style={{ height: '60vh' }}>
            <div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
          </div>
        }>
          <Outlet />
        </Suspense>
        <DemoTour />
      </div>

      {/* Splash мини-празднования поверх всего */}
      {milestone && <CelebrationModal milestone={milestone} onClose={dismissMilestone} />}

      {/* Notification Center Drawer */}
      <NotificationCenter
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        onCloseSidebar={closeSidebar}
        notifications={notifications}
        unread={unread}
        loading={loading}
        markAllRead={markAllRead}
        markOneRead={markOneRead}
        refresh={refreshNotifications}
      />

      {/* Demo exit modal — установка пароля */}
      {demoExitModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: 16,
        }}>
          <div style={{
            background: 'var(--bg)', borderRadius: 'var(--radius)',
            padding: 28, maxWidth: 400, width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>{t('demo.exitModal.title')}</h3>
            <p style={{ margin: '0 0 20px', color: 'var(--text2)', fontSize: 14, lineHeight: 1.5 }}>
              {t('demo.exitModal.description')}
            </p>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                {t('demo.exitModal.passwordLabel')}
              </label>
              <input
                type="password"
                className="input"
                placeholder={t('demo.exitModal.passwordPlaceholder')}
                value={demoPassword}
                onChange={e => { setDemoPassword(e.target.value); setDemoPasswordError(''); }}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                {t('demo.exitModal.passwordConfirmLabel')}
              </label>
              <input
                type="password"
                className="input"
                placeholder={t('demo.exitModal.passwordConfirmPlaceholder')}
                value={demoPasswordConfirm}
                onChange={e => { setDemoPasswordConfirm(e.target.value); setDemoPasswordError(''); }}
                style={{ width: '100%' }}
              />
            </div>

            {demoPasswordError && (
              <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>
                {demoPasswordError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
              <button
                className="btn btn-primary"
                onClick={handleDemoSetPassword}
                disabled={demoPasswordSaving}
                style={{ width: '100%' }}
              >
                {demoPasswordSaving ? t('common.saving') : t('demo.exitModal.saveAndExit')}
              </button>
              <button
                className="btn btn-ghost"
                onClick={handleDemoExitWithoutPassword}
                style={{ width: '100%', color: 'var(--text2)', fontSize: 13 }}
              >
                {t('demo.exitModal.exitWithout')}
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => setDemoExitModal(false)}
                style={{ width: '100%' }}
              >
                {t('demo.exitModal.stay')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Канал: шторка справа, в зоне Центра уведомлений (лениво) */}
      <ChannelHost />
    </div>
    </ChannelProvider>
  );
}
